import { useEffect, useState } from 'react';
import { Environment } from '@/config/environment';
import Script from 'next/script';

type Props = {
  locale?: string;
};

const SINGLE_BUTTON_LOCALES = ['en_GB', 'en_IE'];
const UC_SDP_CONTAINER_CLASS = 'uc-embedding-container';
const UC_SDP_SINGLE_BUTTON_ACCEPT_BUTTON_CLASS = 'uc-embedding-more-info';

const UcBlockScript = ({ locale }: Props) => {
  const [isUcLoaded, setIsUcLoaded] = useState(false);
  const [buttonElement, setButtonElement] = useState<HTMLElement | null>(null);

  const { NEXT_PUBLIC_SDP_LOCALES } = Environment.getBuildtimeEnvVars() as {
    NEXT_PUBLIC_SDP_LOCALES: string[];
  };

  const isSingleButtonLocale = locale ? SINGLE_BUTTON_LOCALES.includes(locale) : false;

  const onButtonClick = (event: Event) => {
    event.stopPropagation();

    if (window.UC_UI) {
      // Open the second layer of the Usercentrics overlay
      window.UC_UI.showSecondLayer();
    }
  };

  // Use MutationObserver to detect when the Usercentrics SDP elements are added to the DOM
  // and set the button element when it appears.
  // This is necessary because the button may not be present immediately when the script loads.
  useEffect(() => {
    let observer: MutationObserver | null = null;

    if (isSingleButtonLocale) {
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof HTMLElement && node.className.includes(UC_SDP_CONTAINER_CLASS)) {
                const acceptButton = document.querySelector(
                  `.${UC_SDP_SINGLE_BUTTON_ACCEPT_BUTTON_CLASS}`,
                );

                if (acceptButton) {
                  setButtonElement(acceptButton as HTMLElement);
                }
              }
            });
          }
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer && observer.disconnect();
    };
  }, [isSingleButtonLocale]);

  // Replace the button element with a clone to remove existing event listeners
  // and add a new click event listener to it.
  useEffect(() => {
    if (buttonElement) {
      // Remove existing click event listeners to avoid duplicates
      buttonElement.replaceWith(buttonElement.cloneNode(true));

      // Re-select the button element after replacing it
      const newButtonElement = document.querySelector(
        `.${UC_SDP_SINGLE_BUTTON_ACCEPT_BUTTON_CLASS}`,
      );

      // Add click event listener to the accept button
      newButtonElement?.addEventListener('click', onButtonClick);
    }
  }, [buttonElement]);

  useEffect(() => {
    if (isUcLoaded && window.uc) {
      if (isSingleButtonLocale) {
        window.uc.changeOverlayStyle('single-button');
        window.uc.setCustomTranslations({
          en: {
            ACCEPT: 'Review cookie settings',
            VIDEO_DESCRIPTION:
              "There's a YouTube video on this page. You can't access it because of your cookie settings.",
            DEFAULT_TITLE: 'How to watch this YouTube video',
          },
        });
      }

      window.uc.blockOnly(['BJz7qNsdj-7']);
    }
  }, [isSingleButtonLocale, isUcLoaded]);

  const scriptLoaded = () => {
    setIsUcLoaded(true);
  };

  // Check if the locale is supported and if the Usercentrics SDP script should be loaded
  const isLocaleSupported = locale ? NEXT_PUBLIC_SDP_LOCALES?.includes(locale) : false;

  // TODO: Find a wat to load script inside document
  // Currently, it only works when putting the script inside _app.tsx
  return (
    isLocaleSupported && (
      // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
      <Script
        src="https://privacy-proxy.usercentrics.eu/latest/uc-block.bundle.js"
        onReady={scriptLoaded}
        strategy="beforeInteractive"
      />
    )
  );
};

export default UcBlockScript;
